import { Section } from "./Sections";

type Review = {
  author: string;
  rating: number;
  date: string;
  source: "Trustpilot" | "Google";
  text: string;
};

// Reviews from our sister company SAK Electric A/S (same owners, same elektrikere).
// Displayed as content with clear source attribution — NOT marked up as schema
// to avoid Google rich-snippet penalties (different CVR / juridisk enhed).
const REVIEWS: Review[] = [
  {
    author: "Mette H.",
    rating: 5,
    date: "2024",
    source: "Trustpilot",
    text: "Super hurtig respons og venlig elektriker. Fik installeret ladestander til elbilen samme uge, alt fungerede perfekt og prisen var som aftalt.",
  },
  {
    author: "Lars P.",
    rating: 5,
    date: "2024",
    source: "Trustpilot",
    text: "Akut udrykning en søndag aften da HPFI'en blev ved at slå fra. Fejlen blev fundet og udbedret på under en time. Professionelt og venligt.",
  },
  {
    author: "Anne K.",
    rating: 5,
    date: "2024",
    source: "Google",
    text: "Brugte dem til komplet el-renovering af lejlighed på Østerbro. Fast pris fra start, mødte til tiden hver dag og afleverede et flot resultat.",
  },
  {
    author: "Thomas B.",
    rating: 5,
    date: "2023",
    source: "Trustpilot",
    text: "Dygtige elektrikere der ved hvad de laver. Fik lavet ny eltavle og rengøring af installation, alt blev dokumenteret og forklaret undervejs.",
  },
  {
    author: "Sofie L.",
    rating: 5,
    date: "2024",
    source: "Google",
    text: "Hurtigt tilbud, fair pris og dygtigt udført arbejde. Anbefales klart til andre der søger en pålidelig elektriker i København.",
  },
  {
    author: "Henrik J.",
    rating: 4,
    date: "2024",
    source: "Trustpilot",
    text: "God service og kompetent rådgivning omkring valg af ladeboks. Lille forsinkelse på montagedag, men ellers intet at klage på.",
  },
];

function Stars({ value }: { value: number }) {
  return (
    <div role="img" aria-label={`${value} ud af 5 stjerner`} className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={i < value ? "#00B67A" : "var(--border)"}
          aria-hidden
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

export function ReviewsSection() {
  return (
    <Section>
      <div className="mb-8 max-w-3xl">
        <span className="inline-flex w-fit items-center rounded-full bg-secondary px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--ink)]">
          Anmeldelser
        </span>
        <h2 className="mt-4 text-3xl md:text-4xl">Hvad vores kunder siger</h2>
        <p className="mt-3 text-muted-foreground">
          Anmeldelserne nedenfor er hentet fra vores søsterselskab{" "}
          <strong className="text-[var(--ink)]">SAK Electric A/S</strong> — samme
          ejerkreds, samme elektrikere og samme kvalitetsstandard som
          Elektriker-Elektriker ApS. Vi viser dem som reference; de er ikke
          juridisk knyttet til Elektriker-Elektriker ApS (CVR 35209468).
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <article
            key={i}
            className="flex flex-col rounded-2xl border border-border bg-card p-6"
          >
            <div className="mb-3 flex items-center justify-between">
              <Stars value={r.rating} />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {r.source}
              </span>
            </div>
            <p className="flex-1 text-sm leading-relaxed text-[var(--ink)]/85">
              &ldquo;{r.text}&rdquo;
            </p>
            <div className="mt-4 text-xs text-muted-foreground">
              — {r.author}, {r.date}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <a
          href="https://dk.trustpilot.com/review/www.sak-electric.dk"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary"
        >
          Se alle anmeldelser på Trustpilot →
        </a>
        <a
          href="https://www.google.com/search?q=SAK+Electric+A%2FS+anmeldelser"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary"
        >
          Se anmeldelser på Google →
        </a>
      </div>
    </Section>
  );
}
